import assetsBase from '@exodus/assets-base'
import { create } from 'superstruct'
import { BuyOrder } from '../schema/order.js'
import { createPaymentMethodValidator } from '../payment-method.js'
import { Quote } from '../schema/quote.js'
import { ORDER_PAYMENT_METHODS, PAYMENT_METHOD_TYPES, PROVIDERS } from '../constants.js'
import { ENABLED_ASSETS, PAYMENT_METHODS, PAYMENT_METHOD_NAMES } from './constants.js'

const assets = assetsBase.default || assetsBase

const fromProviderToExodusAsset = (asset, network, assets_ = ENABLED_ASSETS) =>
  assets[assets_[`${asset.toLowerCase()}_${network ? network.toLowerCase() : asset.toLowerCase()}`]]

const fromExodusToProviderAsset = (asset, assets_ = ENABLED_ASSETS) =>
  Object.fromEntries(Object.entries(assets_).map((entry) => entry.reverse()))
    [asset]?.toUpperCase()
    ?.split('_') ?? []

const fromProviderToExodusPaymentMethod = (paymentMethod) => {
  if (!paymentMethod) {
    throw new Error('Payment method is required')
  }

  const type = PAYMENT_METHODS.get(paymentMethod)?.type
  return Array.isArray(type) ? type[0] : type
}

const fromExodusToProviderPaymentMethod = (paymentMethod, parameters = {}) => {
  const providerPaymentMethod = [...PAYMENT_METHODS.values()].find(
    createPaymentMethodValidator({ ...parameters, type: paymentMethod })
  )

  return providerPaymentMethod?.id
}

const fromExodusOrderPaymentMethodToExodusClientPaymentMethod = (paymentMethod) =>
  ({
    [ORDER_PAYMENT_METHODS.CARD_PAYMENT]: PAYMENT_METHOD_TYPES.card,
  })[paymentMethod] ?? ORDER_PAYMENT_METHODS.MANUAL_BANK_TRANSFER

const fromExodusOrderPaymentMethodToProviderPaymentMethod = (paymentMethod) =>
  ({
    [ORDER_PAYMENT_METHODS.CARD_PAYMENT]: PAYMENT_METHOD_NAMES.card,
  })[paymentMethod] ?? ORDER_PAYMENT_METHODS.MANUAL_BANK_TRANSFER

const fromProviderToExodusOrder = (order) => {
  const asset =
    fromProviderToExodusAsset(order.toAsset) ?? fromProviderToExodusAsset(order.toAsset, 'eth')

  return create(
    {
      createdAt: new Date(order.createdAt),
      extraFee: 0,
      fee: order.providerFee,
      from: {
        amount: order.fromAmount,
        asset: order.fromAsset,
      },
      id: order.id,
      networkFee: order.networkFee,
      paymentMethod: fromExodusOrderPaymentMethodToProviderPaymentMethod(order.paymentMethod),
      paymentMethodType: fromExodusOrderPaymentMethodToExodusClientPaymentMethod(
        order.paymentMethod
      ),
      provider: PROVIDERS.blockchain,
      providerId: order.providerId,
      rate: order.exchangeRate,
      status: order.status.toUpperCase(),
      to: {
        amount: order.toAmount,
        asset: asset.name,
      },
      totalFee: order.totalFee,
      txId: order.txId,
      updatedAt: new Date(order.updatedAt),
      walletAddress: order.walletAddress,
    },
    BuyOrder
  )
}

const fromProviderToExodusQuote = async (quote) => {
  const fee = Number(quote.processingFeeAmount)
  const networkFee = Number(quote.networkFeeAmount)
  const partnerFee = Number(quote.partnerFeeAmount)
  const totalFeeFiat = fee + networkFee + partnerFee
  const cryptoCurrencyAmount = Number(quote.baseCurrencyAmount)
  const cryptoCurrencyAmountMinusFee = Number(quote.totalAmount)
  const fiatCurrencyAmount = Number(quote.quoteCurrencyAmount)
  const totalFeeCrypto = Number.isNaN(totalFeeFiat)
    ? undefined
    : (cryptoCurrencyAmount * totalFeeFiat) / fiatCurrencyAmount

  const from = quote.quoteCurrency
  const to = quote.baseCurrency

  return create(
    {
      extraFee: 0,
      fee,
      from: {
        amount: fiatCurrencyAmount,
        asset: quote.fromAsset,
        maxBuyAmount: Number(from.maxBuyAmount),
        minBuyAmount: Number(from.minBuyAmount),
        precision: from.precision,
      },
      networkFee,
      paymentMethod: quote.paymentMethod,
      provider: PROVIDERS.blockchain,
      rate: Number(quote.price),
      to: {
        amount: cryptoCurrencyAmountMinusFee,
        asset: quote.toAsset,
        precision: to.precision,
      },
      totalAmount: Number(quote.totalAmount),
      totalFee: totalFeeFiat,
      totalFeeCrypto,
      type: quote.type,
    },
    Quote
  )
}

const blockchainAdapter = {
  fromExodusToProviderAsset,
  fromProviderToExodusPaymentMethod,
  fromExodusToProviderPaymentMethod,
  fromExodusOrderPaymentMethodToProviderPaymentMethod,
  fromProviderToExodusAsset,
  fromProviderToExodusOrder,
  fromProviderToExodusQuote,
}

export default blockchainAdapter
